"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Pencil, Trash2, Plus } from "lucide-react";
import { PricingService } from "@/lib/pricing/pricing-service-unified";
import { getCurrencySymbol } from "@/lib/utils/currency";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

interface ShopItem {
  id: number;
  shop_item_id: number;
  assigned_pricing_id?: number;
  quantity: number;
  amount: number;
  total_price: number;
  vat_rate?: number; // Stored VAT rate from database
  vat_amount?: number; // Actual VAT amount from database
  total_price_excl_vat?: number; // Price excluding VAT
  total_price_incl_vat?: number; // Price including VAT
  assigned_pricing?: {
    id: number;
    price: number;
    vat_percentage?: number;
  };
  shop_items: {
    id: number;
    name: string;
    pricing_id: number;
    pricing?: {
      id: number;
      price: number;
      vat_percentage?: number;
    };
  };
}

interface ShopItemsCardProps {
  bookingId: string;
  venueId: string;
  shopItems: ShopItem[];
  currency?: string;
  booking: any; // For customer type detection
  onUpdate: () => void;
  onOptimisticUpdate?: (shopItems: ShopItem[]) => void;
  hideActions?: boolean;
}

export function ShopItemsCard({
  bookingId,
  venueId,
  shopItems,
  currency = "SEK",
  booking,
  onUpdate,
  onOptimisticUpdate,
  hideActions = false,
}: ShopItemsCardProps) {
  const [localShopItems, setLocalShopItems] = useState<ShopItem[]>(shopItems);

  // Update local state when props change
  useEffect(() => {
    setLocalShopItems(shopItems);
  }, [shopItems]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedShopItem, setSelectedShopItem] = useState<string>("");
  const [quantity, setQuantity] = useState<string>("1");
  const [priceOverride, setPriceOverride] = useState<string>("");
  const [sendEmail, setSendEmail] = useState(false);
  const [availableShopItems, setAvailableShopItems] = useState<any[]>([]);
  const [editingShopItem, setEditingShopItem] = useState<ShopItem | null>(null);
  const [deletingShopItem, setDeletingShopItem] = useState<ShopItem | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);

  // Fetch available shop items when modal opens
  const handleOpenAddModal = async () => {
    if (hideActions) return;
    setIsLoading(true);
    try {
      const response = await fetch(`/api/venues/${venueId}/shop-items`);
      if (!response.ok) {
        throw new Error(`Failed to load shop items: ${response.statusText}`);
      }
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Server returned non-JSON response");
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        setAvailableShopItems(data);
      } else {
        toast.error("Invalid shop items data format");
      }
    } catch (error) {
      toast.error("Failed to load shop items");
    } finally {
      setIsLoading(false);
      setIsAddModalOpen(true);
    }
  };

  const handleAddShopItem = async () => {
    if (hideActions) return;
    if (!selectedShopItem) {
      toast.error("Please select a shop item");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`/api/bookings/${bookingId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "add-shop-item",
          shop_item_id: parseInt(selectedShopItem),
          quantity: parseInt(quantity) || 1,
          price_override: priceOverride ? parseFloat(priceOverride) : undefined,
          send_email: sendEmail,
        }),
      });

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const error = await response.json();
          throw new Error(
            error.error || `Failed to add shop item: ${response.statusText}`,
          );
        } else {
          throw new Error(
            `Failed to add shop item: ${response.status} ${response.statusText}`,
          );
        }
      }

      const data = await response.json();
      if (data.success) {
        toast.success("Shop item added successfully");
        setIsAddModalOpen(false);
        resetForm();
        // Use lighter update approach - only refresh if no optimistic update available
        onUpdate();
      } else {
        toast.error(data.error || "Failed to add shop item");
      }
    } catch (error) {
      toast.error("Failed to add shop item");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditShopItem = (shopItem: ShopItem) => {
    if (hideActions) return;
    setEditingShopItem(shopItem);
    setQuantity(shopItem.quantity.toString());
    // Use amount if > 0, otherwise use assigned pricing price
    const price =
      shopItem.amount > 0
        ? shopItem.amount
        : shopItem.assigned_pricing?.price || 0;
    setPriceOverride(price.toString());
    setIsEditModalOpen(true);
  };

  const handleUpdateShopItem = async () => {
    if (hideActions) return;
    if (!editingShopItem) return;

    setIsLoading(true);
    try {
      const response = await fetch(`/api/bookings/${bookingId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "update-shop-item",
          shop_item_booking_id: editingShopItem.id,
          quantity: parseInt(quantity) || 1,
          price_override: priceOverride ? parseFloat(priceOverride) : undefined,
        }),
      });

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const error = await response.json();
          throw new Error(
            error.error || `Failed to update shop item: ${response.statusText}`,
          );
        } else {
          throw new Error(
            `Failed to update shop item: ${response.status} ${response.statusText}`,
          );
        }
      }

      const data = await response.json();
      if (data.success) {
        toast.success("Shop item updated successfully");
        setIsEditModalOpen(false);
        resetForm();
        // Use lighter update approach - only refresh if no optimistic update available
        onUpdate();
      } else {
        toast.error(data.error || "Failed to update shop item");
      }
    } catch (error) {
      toast.error("Failed to update shop item");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveShopItem = (shopItem: ShopItem) => {
    if (hideActions) return;
    setDeletingShopItem(shopItem);
    setIsDeleteModalOpen(true);
  };

  const confirmRemoveShopItem = async () => {
    if (hideActions) return;
    if (!deletingShopItem) return;

    // Optimistic update: remove item immediately from UI
    const updatedItems = localShopItems.filter(
      (item) => item.id !== deletingShopItem.id,
    );
    setLocalShopItems(updatedItems);

    // Close modal immediately for better UX
    setIsDeleteModalOpen(false);
    setDeletingShopItem(null);

    // Notify parent with optimistic update
    if (onOptimisticUpdate) {
      onOptimisticUpdate(updatedItems);
    }

    try {
      const response = await fetch(`/api/bookings/${bookingId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "remove-shop-item",
          shop_item_booking_id: deletingShopItem.id,
        }),
      });

      if (!response.ok) {
        // Revert optimistic update on error
        setLocalShopItems(shopItems);

        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const error = await response.json();
          throw new Error(
            error.error || `Failed to remove shop item: ${response.statusText}`,
          );
        } else {
          throw new Error(
            `Failed to remove shop item: ${response.status} ${response.statusText}`,
          );
        }
      }

      const data = await response.json();
      if (data.success) {
        toast.success("Shop item removed successfully");
        // Only call onUpdate for final data sync if needed
        // onUpdate(); // Commenting out to prevent full reload
      } else {
        // Revert optimistic update on API error
        setLocalShopItems(shopItems);
        toast.error(data.error || "Failed to remove shop item");
      }
    } catch (error) {
      // Revert optimistic update on network error
      setLocalShopItems(shopItems);
      toast.error("Failed to remove shop item");
    }
  };

  const resetForm = () => {
    setSelectedShopItem("");
    setQuantity("1");
    setPriceOverride("");
    setSendEmail(false);
    setEditingShopItem(null);
  };

  const selectedShopItemData = availableShopItems.find(
    (item) => item.id === parseInt(selectedShopItem),
  );
  const originalPrice =
    selectedShopItemData?.pricing?.price ||
    selectedShopItemData?.current_price ||
    0;
  const originalTotal = originalPrice * parseInt(quantity || "0");
  const newTotal = priceOverride
    ? parseFloat(priceOverride) * parseInt(quantity || "0")
    : originalTotal;
  const priceDifference = newTotal - originalTotal;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-semibold text-gray-900">
          Shop Items
        </CardTitle>
        {!hideActions && (
          <Button
            onClick={handleOpenAddModal}
            className="bg-white border border-purple-600 text-purple-600 hover:bg-purple-50"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Item
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {localShopItems.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            No shop items added
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item Name</TableHead>
                <TableHead>Quantity</TableHead>
                <TableHead>Unit Price</TableHead>
                <TableHead>VAT</TableHead>
                <TableHead>Total</TableHead>
                {!hideActions && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {localShopItems.map((item) => {
                // Use stored VAT data from database instead of recalculating
                const vatPercentage =
                  item.vat_rate ||
                  item.assigned_pricing?.vat_percentage ||
                  item.shop_items.pricing?.vat_percentage ||
                  0;
                const isPrivateBooking = booking.customer_type_id === 1;
                const unitPrice =
                  item.amount > 0
                    ? item.amount
                    : item.assigned_pricing?.price || 0;

                // Use actual VAT amounts from database when available
                const vatAmount = item.vat_amount || 0;
                const totalExclVat =
                  item.total_price_excl_vat ||
                  item.total_price ||
                  unitPrice * item.quantity;
                const totalInclVat =
                  item.total_price_incl_vat || totalExclVat + vatAmount;

                // For display: show the actual VAT amount from database
                const invoiceVatAmount = vatAmount;

                return (
                  <TableRow key={item.id}>
                    <TableCell>
                      <div className="font-medium">{item.shop_items.name}</div>
                    </TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>
                      {unitPrice.toFixed(2)} {getCurrencySymbol(currency)}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div>
                          {invoiceVatAmount.toFixed(2)} {getCurrencySymbol(currency)}
                        </div>
                        <div className="text-gray-500">({vatPercentage}%)</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {totalExclVat.toFixed(2)} {getCurrencySymbol(currency)}
                    </TableCell>
                    {!hideActions && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditShopItem(item)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveShopItem(item)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Add Shop Item Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Shop Item</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="item-select">Select Shop Item</Label>
              <Select
                value={selectedShopItem}
                onValueChange={setSelectedShopItem}
              >
                <SelectTrigger id="item-select">
                  <SelectValue placeholder="Select a shop item" />
                </SelectTrigger>
                <SelectContent>
                  {availableShopItems.map((item) => (
                    <SelectItem key={item.id} value={item.id.toString()}>
                      {item.name} - {item.pricing?.price || 0} {getCurrencySymbol(currency)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="quantity">Quantity</Label>
              <Input
                id="quantity"
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                min="1"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="price-override">Price Override (per unit)</Label>
              <Input
                id="price-override"
                type="number"
                value={priceOverride}
                onChange={(e) => setPriceOverride(e.target.value)}
                placeholder={originalPrice.toString()}
              />
            </div>

            {selectedShopItemData && (
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Original Price</span>
                  <span>
                    {originalTotal} {getCurrencySymbol(currency)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>New Price</span>
                  <span>
                    {newTotal} {getCurrencySymbol(currency)}
                  </span>
                </div>
                {priceDifference !== 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Difference</span>
                    <span>
                      {priceDifference > 0 ? "+" : ""}
                      {priceDifference} {getCurrencySymbol(currency)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddShopItem} disabled={isLoading}>
              {isLoading ? "Adding..." : "Add Item"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Shop Item Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Shop Item</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Shop Item</Label>
              <div className="p-2 bg-gray-50 rounded">
                {editingShopItem?.shop_items.name}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-quantity">Quantity</Label>
              <Input
                id="edit-quantity"
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                min="1"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-price">Price Override (per unit)</Label>
              <Input
                id="edit-price"
                type="number"
                value={priceOverride}
                onChange={(e) => setPriceOverride(e.target.value)}
              />
            </div>

            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span>Total Price</span>
                <span>
                  {(
                    parseFloat(priceOverride || "0") * parseInt(quantity || "0")
                  ).toFixed(2)}{" "}
                  {getCurrencySymbol(currency)}
                </span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateShopItem} disabled={isLoading}>
              {isLoading ? "Updating..." : "Update Item"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Shop Item</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this shop item from the booking?
            </DialogDescription>
          </DialogHeader>

          {deletingShopItem && (
            <div className="py-4">
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="font-medium">
                  {deletingShopItem.shop_items.name}
                </div>
                <div className="text-sm text-muted-foreground">
                  {(() => {
                    const unitPrice =
                      deletingShopItem.amount > 0
                        ? deletingShopItem.amount
                        : deletingShopItem.assigned_pricing?.price || 0;
                    const total = unitPrice * deletingShopItem.quantity;
                    return `Quantity: ${deletingShopItem.quantity} × ${unitPrice} ${currency} = ${total} ${currency}`;
                  })()}
                </div>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">
                This action will archive the shop item. It can be restored if
                needed.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setDeletingShopItem(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmRemoveShopItem}
              disabled={isLoading}
            >
              {isLoading ? "Removing..." : "Remove Shop Item"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
